import { Dropdown } from 'antd';
import { PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { Icon, Link, Button, Checkbox } from 'components/common';
import { Fragment, useCallback, useMemo } from 'react';
import { colours } from 'utils/theme';
import { views } from '../constants';
import { useDispatch, useSelector } from 'react-redux';
import { toggleLists } from 'actions/lists';
import { getSelectedLists } from 'selectors/lists';
import {
  getAreAllSelected,
  getAreSomeSelected,
} from 'components/common/Table/MultiSelect/helpers';
import Export from 'components/Export';
import { downloadType } from 'components/Export/Summary/constants';
import {
  hasPermissionToPushExport,
  canModify,
  canPushToCrm,
} from './permissionsHelper';
import { crmTypes } from 'utils/helpers/crm';
import EnrichmentIcon from 'components/common/Icon/EnrichmentIcon';
import styled from 'styled-components';
import useExport from '../Actions/useExport';

const EnrichName = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  word-break: break-all;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  margin-left: auto;

  .hidden-cell-row & {
    margin-left: 0;
  }
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0 2px;
`;

const IconWrapper = styled.div`
  width: 16px;
  overflow: visible;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const useConfig = (
  view,
  data,
  loading,
  user,
  handleEditList,
  handleToggleList,
  handleDeleteList,
  handlePushToCRM
) => {
  const dispatch = useDispatch();
  const selectedMembers = useSelector(getSelectedLists);
  const allSelected = getAreAllSelected(data?.total || 0, selectedMembers);
  const someSelected = getAreSomeSelected(data?.total || 0, selectedMembers);
  const getEditOptions = useCallback(
    list => {
      const options = [];
      const canModifyList = canModify(list, user);
      if (canModifyList) {
        options.push({
          label: (
            <OptionContainer onClick={() => handleEditList(list)}>
              <IconWrapper>
                <Icon
                  icon='settings'
                  size='1x'
                  style={{ color: 'currentColor' }}
                />
              </IconWrapper>
              List Settings
            </OptionContainer>
          ),
          key: 0,
        });
        options.push({
          label: (
            <OptionContainer onClick={() => handleToggleList(list)}>
              {list?.enabled ? (
                <>
                  <IconWrapper>
                    <PauseCircleOutlined />
                  </IconWrapper>
                  Pause List
                </>
              ) : (
                <>
                  <IconWrapper>
                    <PlayCircleOutlined />
                  </IconWrapper>
                  Resume List
                </>
              )}
            </OptionContainer>
          ),
          key: 1,
        });
      }
      if (list.contacts > 0 && view === views.researchers) {
        if (canPushToCrm(list, user, crmTypes.SALESFORCE)) {
          options.push({
            label: (
              <OptionContainer
                onClick={() => handlePushToCRM(crmTypes.SALESFORCE, list)}
              >
                <IconWrapper>
                  <Icon icon='salesforce' color={colours.black} size='1x' />
                </IconWrapper>
                Push to Salesforce
              </OptionContainer>
            ),
            key: 2,
          });
        }
        if (canPushToCrm(list, user, crmTypes.HUBSPOT)) {
          options.push({
            label: (
              <OptionContainer
                onClick={() => handlePushToCRM(crmTypes.HUBSPOT, list)}
              >
                <IconWrapper>
                  <Icon icon='hubspot' color={colours.black} size='1x' />
                </IconWrapper>
                Push to Hubspot
              </OptionContainer>
            ),
            key: 3,
          });
        }
      }

      if (canModifyList) {
        options.push({
          label: (
            <OptionContainer onClick={() => handleDeleteList(list)}>
              <IconWrapper>
                <Icon icon='trash' color={colours.black} size='1x' />
              </IconWrapper>
              Delete List
            </OptionContainer>
          ),
          key: 4,
        });
      }

      return options;
    },
    [
      handleDeleteList,
      handleEditList,
      handleToggleList,
      handlePushToCRM,
      user,
      view,
    ]
  );

  const [getExportBody, handleExport] = useExport(view, true);
  const config = useMemo(
    () => ({
      headers: [
        {
          testId: 'checkbox',
          label: (
            <Checkbox
              checked={allSelected}
              disabled={loading}
              indeterminate={someSelected}
              onClick={() =>
                dispatch(
                  toggleLists({
                    ids: data?.data.map(x => x.id),
                    allOnPage: true,
                  })
                )
              }
            />
          ),
          minWidth: 20,
        },
        {
          label: 'List Name',
          key: 'name',
          minWidth: 100,
          maxContentWidth: 250,
        },
        {
          label: view === views.researchers ? 'Contacts' : 'Organisations',
          key: 'contacts',
          minWidth: 50,
        },
        { label: 'Last Updated', key: 'lastUpdatedUtc', minWidth: 100 },
        { label: 'Date Created', key: 'dateCreatedUtc', minWidth: 100 },
        { label: 'Owner', key: 'createdByUserName', minWidth: 100 },
        { label: 'List Status', key: 'enabled', minWidth: 50 },
        { key: 'actions', minWidth: 100 },
      ],
      createRow: (r, i) => {
        const editOptions = getEditOptions(r);
        return (
          <Fragment key={`${r.name}-${i}`}>
            <div>
              <Checkbox
                checked={
                  !selectedMembers.exclude[r.id] &&
                  (selectedMembers.include[r.id] || selectedMembers.all)
                }
                onClick={() => dispatch(toggleLists({ id: r.id }))}
              />
            </div>
            <div>
              <div>
                <Link
                  to={
                    view === views.researchers
                      ? `lists/researcher/${r.id}`
                      : `lists/organisation/${r.id}`
                  }
                >
                  <EnrichName>
                    {r.name}
                    {r.dataEnrichmentId && <EnrichmentIcon />}
                  </EnrichName>
                </Link>
                {r.description && <div>{r.description}</div>}
              </div>
            </div>
            <div>{r.contacts}</div>
            <div>{r.lastUpdated}</div>
            <div>{r.dateCreated}</div>
            <div>{r.createdByUserName}</div>
            <div>{r.enabled ? 'Active' : 'Paused'}</div>
            <ButtonsWrapper>
              {editOptions.length > 0 && (
                <Dropdown
                  menu={{
                    items: editOptions,
                  }}
                  trigger={['click']}
                >
                  <Button size='small'>...</Button>
                </Dropdown>
              )}
              {hasPermissionToPushExport(user) && (
                <Export
                  downloadType={
                    view === views.researchers
                      ? downloadType.researcherList
                      : downloadType.organisationList
                  }
                  getQueryBody={params => {
                    const bodyParams = params || {};
                    bodyParams.listId = r.id;
                    return getExportBody(bodyParams);
                  }}
                  disabled={r.contacts <= 0 || loading}
                  onExport={params => {
                    const bodyParams = params || {};
                    bodyParams.listId = r.id;
                    return handleExport(bodyParams);
                  }}
                  icon={
                    <Icon
                      icon='download'
                      color={colours.black}
                      size='1x'
                      style={{ marginRight: '8px', color: 'currentColor' }}
                    />
                  }
                  canUseCredits={view === views.researchers}
                  size='small'
                />
              )}
            </ButtonsWrapper>
          </Fragment>
        );
      },
    }),
    [
      allSelected,
      data?.data,
      dispatch,
      getEditOptions,
      loading,
      selectedMembers.all,
      selectedMembers.exclude,
      selectedMembers.include,
      someSelected,
      user,
      view,
      getExportBody,
      handleExport,
    ]
  );
  return config;
};
